import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import {
    Box,
    IconButton,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import {
    Border,
    FontVariantToken,
    Spacing,
} from '@uipath/apollo-core';
import {
    ApSkeleton,
    ApTooltip,
    ApTypography,
} from '@uipath/portal-shell-react';
import React from 'react';
import { useIntl } from 'react-intl';

const useStyles = makeStyles(theme =>
    createStyles({
        card: {
            flex: 1,
            minHeight: 460,
            borderRadius: Border.BorderRadiusL,
            border: Border.BorderLight,
            backgroundColor: theme.palette.semantic.colorBackground,
            padding: Spacing.SpacingL,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
        },
        cardHeader: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: Spacing.SpacingBase,
        },
        cardTitle: { color: theme.palette.semantic.colorForeground },
        contentArea: {
            minHeight: 0,
            flex: 1,
            overflow: 'hidden',
        },
        downloadIconButton: {
            color: theme.palette.semantic.colorForegroundDeEmp,
            '&:hover': {
                color: theme.palette.semantic.colorForeground,
                backgroundColor: theme.palette.semantic.colorBackgroundHover,
            },
        },
        skeletonDownloadButton: {
            maxWidth: '5%',
            borderRadius: Border.BorderRadiusM,
        },
    })
);

interface ConsumptionAnalyticsCardProps {
    titleId: string;
    isLoading?: boolean;
    disableDownload?: boolean;
    children?: React.ReactNode;
    onDownload?: () => void;
}

export const ConsumptionAnalyticsCard: React.FC<ConsumptionAnalyticsCardProps> = ({
    titleId,
    isLoading = false,
    disableDownload = false,
    children,
    onDownload,
}) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();

    if (isLoading) {
        return (
            <Box className={classes.card}>
                <Box className={classes.cardHeader}>
                    <ApTypography
                        variant={FontVariantToken.fontSizeH4Bold}
                        className={classes.cardTitle}
                    >
                        {translate({ id: titleId })}
                    </ApTypography>
                    <ApSkeleton
                        variant="rectangle"
                        className={classes.skeletonDownloadButton}
                    />
                </Box>

                <Box className={classes.contentArea}>
                    {children}
                </Box>
            </Box>
        );
    }

    return (
        <Box className={classes.card}>
            <Box className={classes.cardHeader}>
                <ApTypography
                    variant={FontVariantToken.fontSizeH4Bold}
                    className={classes.cardTitle}
                >
                    {translate({ id: titleId })}
                </ApTypography>
                <ApTooltip content={translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_DOWNLOAD' })}>
                    <span>
                        <IconButton
                            size="small"
                            className={classes.downloadIconButton}
                            disabled={disableDownload}
                            onClick={onDownload}
                        >
                            <DownloadOutlinedIcon fontSize="small" />
                        </IconButton>
                    </span>
                </ApTooltip>
            </Box>

            <Box className={classes.contentArea}>
                {children}
            </Box>
        </Box>
    );
};
